import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { RootState } from "app/store";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

import {
  RECORDING_STATE_IN_PROGRESS,
  RECORDING_STATE_PAUSED,
  RECORDING_STATE_SAVING,
  RECORDING_STATE_STOPPED
} from "./medical-scribe-constants";

export interface TranscriptEntry {
  speaker: number;
  text: string;
  isFinal: boolean;
  timestamp?: number;
}

interface MedicalScribeState {
  recordingState: number;
  chartNoteId: number | undefined;
  patientId: number | undefined;
  showSilenceWarningDialog?: boolean;
  //
  isConnecting?: boolean;
  isAuthenticated?: boolean;
  isReconnecting?: boolean;
  isPaused?: boolean;
  error?: string;
  wasResumed?: boolean;
  scribeId: number | undefined;
  transcript?: TranscriptEntry[];
  currentSessionNumber?: number;
  //
  audioUrl: string | undefined;
  showDeviceSettingsDialog: boolean;
  volume: number;
  isMicActive: boolean;
  deviceSettings: {
    microphone: string;
    speaker: string;
    showTranscriptionPreview?: boolean;
    autoGenerateSOAPNotes?: boolean;
    autoFindRelatedChartNote?: boolean;
  };
  isConfirmationDialogOpen: boolean;
  scribeFeedbackDialog?: {
    feedbackType?: number;
    scribeId?: number;
    rating?:number;
    open?: boolean;
  };
}

const medicalScribeMicrophoneStorage = setupLocalStorageAPI(
  "medicalScribeMicrophone"
);

const medicalScribeSpeakerStorage = setupLocalStorageAPI(
  "medicalScribeSpeaker"
);

const medicalScribeShowTranscriptionPreviewStorage = setupLocalStorageAPI(
  "medicalScribeShowTranscriptionPreview"
);

const medicalScribeAutoGenerateSOAPNotesStorage = setupLocalStorageAPI(
  "medicalScribeAutoGenerateSOAPNotes"
);

const medicalScribeAutoFindRelatedChartNoteStorage = setupLocalStorageAPI(
  "medicalScribeAutoFindRelatedChartNote"
);

const initialState: MedicalScribeState = {
  recordingState: RECORDING_STATE_STOPPED,
  chartNoteId: undefined,
  patientId: undefined,
  showSilenceWarningDialog: false,
  isConnecting: false,
  isAuthenticated: false,
  isReconnecting: false,
  isPaused: false,
  error: undefined,
  wasResumed: false,
  scribeId: undefined,
  transcript: [],
  currentSessionNumber: undefined,
  audioUrl: undefined,
  showDeviceSettingsDialog: false,
  volume: 0,
  isMicActive: false,
  deviceSettings: {
    microphone: medicalScribeMicrophoneStorage.getData("default"),
    speaker: medicalScribeSpeakerStorage.getData("default"),
    showTranscriptionPreview:
      medicalScribeShowTranscriptionPreviewStorage.getData(true),
    autoGenerateSOAPNotes:
      medicalScribeAutoGenerateSOAPNotesStorage.getData(true),
    autoFindRelatedChartNote:
      medicalScribeAutoFindRelatedChartNoteStorage.getData(true)
  },
  isConfirmationDialogOpen: false,
  scribeFeedbackDialog: {
    open: false
  }
};

export const medicalScribeSlice = createSlice({
  name: "medicalScribe",
  initialState,
  reducers: {
    changeScribeDeviceSettings: (
      state,
      action: PayloadAction<{
        microphone: string;
        speaker: string;
        showTranscriptionPreview?: boolean;
        autoGenerateSOAPNotes?: boolean;
        autoFindRelatedChartNote?: boolean;
      }>
    ) => {
      const {
        microphone,
        speaker,
        showTranscriptionPreview,
        autoGenerateSOAPNotes,
        autoFindRelatedChartNote
      } = action.payload;

      state.deviceSettings = {
        microphone,
        speaker,
        showTranscriptionPreview: showTranscriptionPreview ?? true,
        autoGenerateSOAPNotes: autoGenerateSOAPNotes ?? true,
        autoFindRelatedChartNote: autoFindRelatedChartNote ?? true
      };
      medicalScribeMicrophoneStorage.updateData(microphone);
      medicalScribeSpeakerStorage.updateData(speaker);
      medicalScribeShowTranscriptionPreviewStorage.updateData(
        showTranscriptionPreview ?? true
      );
      medicalScribeAutoGenerateSOAPNotesStorage.updateData(
        autoGenerateSOAPNotes ?? true
      );
      medicalScribeAutoFindRelatedChartNoteStorage.updateData(
        autoFindRelatedChartNote ?? true
      );
    },
    startScribeRecording: (
      state,
      action: PayloadAction<{ patientId?: number; chartNoteId?: number }>
    ) => {
      const { patientId, chartNoteId } = action.payload;
      state.recordingState = RECORDING_STATE_IN_PROGRESS;
      state.chartNoteId = chartNoteId;
      state.patientId = patientId;

      if (state.audioUrl) {
        URL.revokeObjectURL(state.audioUrl);
      }
    },
    setShowSilenceWarningDialog: (state, action: PayloadAction<boolean>) => {
      state.showSilenceWarningDialog = action.payload;
    },

    pauseScribeRecording: (state) => {
      state.recordingState = RECORDING_STATE_PAUSED;
    },
    savingInProgressScribeRecording: (state) => {
      state.recordingState = RECORDING_STATE_SAVING;
    },
    resumeScribeRecording: (state) => {
      state.recordingState = RECORDING_STATE_IN_PROGRESS;
    },
    stopScribeRecording: (state, action: PayloadAction<string>) => {
      return { ...initialState, audioUrl: action.payload };
    },
    updateScribeVolume: (
      state,
      action: PayloadAction<{ volume: number; isMicActive: boolean }>
    ) => {
      const { volume, isMicActive } = action.payload;
      state.volume = volume;
      state.isMicActive = isMicActive;
    },
    toggleShowDeviceSettingsDialog: (state) => {
      state.showDeviceSettingsDialog = !state.showDeviceSettingsDialog;
    },
    setOpenConfirmationDialog: (
      state,
      action: PayloadAction<boolean | undefined>
    ) => {
      const value = action.payload;

      if (value === undefined) {
        state.isConfirmationDialogOpen = !state.isConfirmationDialogOpen;
      } else {
        state.isConfirmationDialogOpen = Boolean(value);
      }
    },
    // Streaming-specific actions
    setStreamingConnecting: (state, action: PayloadAction<boolean>) => {
      state.isConnecting = action.payload;
    },
    setStreamingAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setStreamingReconnecting: (state, action: PayloadAction<boolean>) => {
      state.isReconnecting = action.payload;
    },
    setStreamingPaused: (state, action: PayloadAction<boolean>) => {
      state.isPaused = action.payload;
    },
    setStreamingError: (state, action: PayloadAction<string | undefined>) => {
      state.error = action.payload;
    },
    setStreamingScribeId: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      state.scribeId = action.payload;
    },
    setStreamingWasResumed: (state, action: PayloadAction<boolean>) => {
      state.wasResumed = action.payload;
    },
    setStreamingSessionNumber: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      state.currentSessionNumber = action.payload;
    },
    setStreamingTranscript: (
      state,
      action: PayloadAction<TranscriptEntry[]>
    ) => {
      state.transcript = action.payload;
    },
    appendStreamingTranscript: (
      state,
      action: PayloadAction<TranscriptEntry>
    ) => {
      if (!state.transcript) {
        state.transcript = [];
      }
      state.transcript.push(action.payload);
    },
    clearStreamingState: (state) => {
      state.isConnecting = false;
      state.isAuthenticated = false;
      state.isReconnecting = false;
      state.isPaused = false;
      state.error = undefined;
      state.wasResumed = false;
      state.scribeId = undefined;
      state.transcript = [];
      state.currentSessionNumber = undefined;
    },
    openScribeFeedbackDialog: (
      state,
      action: PayloadAction<{ scribeId: number; feedbackType: number; rating?:number }>
    ) => {
      const { scribeId, feedbackType, rating } = action.payload;
      state.scribeFeedbackDialog = {
        scribeId,
        feedbackType,
        rating,
        open: true
      };
    },
    closeScribeFeedbackDialog: (state) => {
      state.scribeFeedbackDialog = {
        open: false
      };
    }
  }
});

export const {
  startScribeRecording,
  pauseScribeRecording,
  resumeScribeRecording,
  savingInProgressScribeRecording,
  stopScribeRecording,
  changeScribeDeviceSettings,
  toggleShowDeviceSettingsDialog,
  updateScribeVolume,
  setOpenConfirmationDialog,
  setStreamingConnecting,
  setStreamingAuthenticated,
  setStreamingReconnecting,
  setStreamingPaused,
  setStreamingError,
  setStreamingScribeId,
  setStreamingWasResumed,
  setStreamingSessionNumber,
  setStreamingTranscript,
  appendStreamingTranscript,
  clearStreamingState,
  setShowSilenceWarningDialog,
  openScribeFeedbackDialog,
  closeScribeFeedbackDialog
} = medicalScribeSlice.actions;

export default medicalScribeSlice.reducer;

const medicalScribeSelector = (state: RootState) => state.medicalScribe;

// Use medicalScribeSelector directly or specific selectors below
export const selectMedicalScribe = medicalScribeSelector;

export const selectShowMedicalScribeSilenceWarningDialog = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.showSilenceWarningDialog
);

export const selectShowMedicalScribeDeviceDialog = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.showDeviceSettingsDialog
);

export const selectIsConfirmationDialogOpen = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.isConfirmationDialogOpen
);

export const selectMedicalScribeMicVolume = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.volume
);

export const selectMedicalScribeIsMicActive = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.isMicActive
);

export const selectMedicalScribeDeviceSettings = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.deviceSettings
);

export const selectMedicalScribeRecordingState = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.recordingState
);

// Streaming-specific selectors
export const selectStreamingIsConnecting = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.isConnecting
);

export const selectStreamingIsAuthenticated = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.isAuthenticated
);

export const selectStreamingIsReconnecting = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.isReconnecting
);

export const selectStreamingIsPaused = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.isPaused
);

export const selectStreamingError = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.error
);

export const selectStreamingScribeId = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.scribeId
);

export const selectStreamingWasResumed = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.wasResumed
);

export const selectStreamingSessionNumber = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.currentSessionNumber
);

export const selectStreamingTranscript = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.transcript
);

export const selectScribeFeedbackDialog = createSelector(
  medicalScribeSelector,
  (medicalScribe) => medicalScribe.scribeFeedbackDialog
);
